import React, { useMemo, useState } from "react";
import { chartSeries } from "./playerModel";
import { Segmented } from "./ScrollTabs";
import "./football-app.css";

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "");

/**
 * Per-match bars from the provider match log, oldest to newest. Games with no
 * recorded value for the chosen metric are drawn as gaps, not zero bars.
 * Scales to its container via viewBox.
 */
export default function PerformanceChart({ games }) {
  const [metric, setMetric] = useState("shots");
  const series = chartSeries[metric];
  const data = useMemo(() => games.slice(0, 12).reverse(), [games]);
  const first = data.map((g) => series.pick(g));
  const second = series.second ? data.map((g) => series.second.pick(g)) : [];
  const max = Math.max(1, ...first.filter((v) => v !== null), ...second.filter((v) => v !== null));
  const W = 340;
  const H = 170;
  const pad = { l: 24, r: 8, t: 14, b: 30 };
  const step = (W - pad.l - pad.r) / Math.max(1, data.length);
  const bw = Math.max(6, Math.min(22, step - 6));
  const y = (v) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const OPTION_LABELS = { shots: "Shots", contributions: "Goals & assists", rating: "Rating", fouls: "Fouls" };
  const options = Object.keys(chartSeries).map((id) => ({ id, label: OPTION_LABELS[id] }));
  const recorded = first.filter((v) => v !== null).length;

  return (
    <div>
      <Segmented options={options} value={metric} onChange={setMetric} label="Chart metric" />
      {recorded === 0 ? (
        <div className="fa-empty">No recorded {series.label.toLowerCase()} in these matches.</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${series.label} per match, last ${data.length} matches`} style={{ width: "100%", height: "auto", marginTop: 8 }}>
          {[0, 0.5, 1].map((t) => (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r} y1={y(max * t)} y2={y(max * t)} stroke="rgba(134,239,172,.12)" />
              <text x={pad.l - 4} y={y(max * t) + 3} textAnchor="end" fontSize="8" fill="#9fb7a8">{series.decimals ? (max * t).toFixed(0) : Math.round(max * t)}</text>
            </g>
          ))}
          {data.map((g, i) => {
            const v = first[i];
            const s = second[i];
            const x = pad.l + i * step + (step - bw) / 2;
            return (
              <g key={`${g.fixture_id}-${i}`}>
                {v !== null && <rect x={x} y={y(v)} width={bw} height={Math.max(1, H - pad.b - y(v))} rx="3" fill="#31d48b" opacity=".85" />}
                {v !== null && <text x={x + bw / 2} y={y(v) - 3} textAnchor="middle" fontSize="8" fill="#f0fdf4">{series.decimals ? v.toFixed(1) : v}</text>}
                {s !== null && s !== undefined && <circle cx={x + bw / 2} cy={y(s)} r="3.2" fill="#e9b44c" stroke="#07110d" strokeWidth="1" />}
                <text x={x + bw / 2} y={H - 16} textAnchor="middle" fontSize="7.5" fill="#9fb7a8">{fmtDay(g.date)}</text>
                <text x={x + bw / 2} y={H - 6} textAnchor="middle" fontSize="7" fill="#9fb7a8">{String(g.opponent || "").slice(0, 5)}</text>
              </g>
            );
          })}
        </svg>
      )}
      {series.second && recorded > 0 && <p className="fa-note">Bars: {series.label} · dots: {series.second.label}. Matches with no recorded value are left blank.</p>}
    </div>
  );
}
